import React, { useEffect, useMemo, useState } from 'react';

import PageWrapper from './PageWrapper';
import Page from './Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import './GroupsHome.scss';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleAccordion,
  BubbleButton,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
} from '@go-client/components/BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import {
  exportGroups,
  getSimpleGroupBookingsByProperty,
  setArchived,
} from '@go-client/lib/api/groupBooking';
import { BareSimpleGroupBooking } from '@go-shared/types/groupBooking';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import Fuse from 'fuse.js';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { canArchiveGroups, canCreateOrEditBookings } from '@go-shared/lib/permissions';
import GroupsNavigation from '@go-client/components/Navigations/GroupsNavigation';
import { camelToSentenceCase, capitalizeClean, capitalize, stripHTML } from '@go-shared/lib/string';
import { GroupsMode } from './GroupsHome';
import GroupBookingForm from '@go-client/components/GroupBookingForm/GroupBookingForm';
import GroupPartyHistory from '@go-client/components/GroupBookingForm/GroupPartyHistory';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { GroupParty } from '@go-shared/types/groupParty';
import { isExternalOrigin } from '@go-shared/lib/origin';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCommonTexts } from '@go-shared/lib/brand';
import { DefaultedText } from '@go-client/components/DefaultedText';

type GroupsPartyTableItem = Omit<GroupParty, 'phone' | 'email' | 'files' | '_id' | 'groupType'> & {
  id: string;
  groupType?: string;
  _group: BareSimpleGroupBooking;
};

type GroupsTableFields = BubbleTableFields<GroupsPartyTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

interface GroupsPartyParams {
  property: string;
}

type GroupsPartyDataBundle = {
  simpleGroupBookings: BareSimpleGroupBooking[];
};

interface GroupsPartyFilters {
  visibleFields: { [key: string]: boolean };
}

type GroupsPartyPageProgs = RouteComponentProps<GroupsPartyParams>;

const GroupsHome: React.FC<GroupsPartyPageProgs> = (props: GroupsPartyPageProgs) => {
  const serverInfo = useServerInfo();
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const [mode, setMode] = useState<GroupsMode>({ type: 'default' });

  const DEFAULT_FILTERS = {
    visibleFields: {
      name: true,
      groupId: true,
      groupType: true,
      contact: true,
      size: true,
      salesOffice: true,
      embark: true,
      debark: true,
      cabinDrops: false,
      diningPreference: false,
      guestExperience: false,
      prepaidPackages: false,
      groupNotes: false,
    },
  };

  const [filters, setFilters] = useState<GroupsPartyFilters>({
    ...DEFAULT_FILTERS,
  });

  const [dataBundle, setDataBundle] = useState<GroupsPartyDataBundle>({
    simpleGroupBookings: [],
  });

  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [activeProperty]);

  const {
    tour_leader,
    tour_leader_cabin,
    group_contact_name,
    embark_date,
    debark_date,
    guests,
    guest_experience,
    hospitality_desk,
    agency_contact_name,
    sales_dept_email,
    sales_dept_phone,
    party_name,
    group_id,
    group_type,
    cabin_drops,
    dining_preference,
    prepaid_packages,
    sales_office,
    booking_no,
    group_notes,
  } = getCommonTexts(
    {
      default: serverInfo.brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  async function fetchData() {
    setLoading(true);

    try {
      const simpleGroupBookings = await getSimpleGroupBookingsByProperty(activeProperty._id);
      simpleGroupBookings.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setDataBundle({ simpleGroupBookings });
      return simpleGroupBookings;
    } finally {
      setLoading(false);
    }
  }

  const fuse = useMemo(() => {
    return new Fuse(dataBundle.simpleGroupBookings, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['party.name', 'party.groupId', 'party.bookingNo'],
    });
  }, [dataBundle.simpleGroupBookings]);

  const fuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const groups = fuseSearchResults || dataBundle.simpleGroupBookings;

  const tableItems: GroupsPartyTableItem[] = groups
    .filter(g => showArchived || !g.archived)
    .map(g => ({
      id: g._id,
      name: g.party.name,
      groupId: g.party.groupId,
      groupType: g.party.groupType?.name,
      contact: g.party.contact,
      size: g.party.size,
      salesOffice: g.salesOffice,
      bookingNumber: g.party.bookingNo,
      embark: g.party.embark,
      debark: g.party.debark,
      cabinDrops: g.party.cabinDrops,
      diningPreference: g.party.diningPreference,
      guestExperience: stripHTML(g.party.guestExperience || ''),
      prepaidPackages: g.party.prepaidPackages,
      notes: stripHTML(g.party.notes),
      groupGuideName: g.party.groupGuideName,
      groupGuideCabin: g.party.groupGuideCabin,
      hospitalityDesk: g.party.hospitalityDesk,
      internalNotes: g.party.internalNotes ?? '',
      bookingNo: g.party.bookingNo ?? '',
      salesEmail: g.party.salesEmail ?? '',
      salesPhone: g.party.salesPhone ?? '',
      balanceDue: g.party.balanceDue,
      paymentStatus: g.party.paymentStatus,
      agencyContactName: g.party.agencyContactName ?? '',
      comments: g.party.comments,
      _group: g,
    }));
  const tableFields: GroupsTableFields[] = [
    {
      key: 'name',
      label: capitalize(party_name),
      _toggleable: true,
    },
    {
      key: 'groupId',
      label: capitalize(group_id),
      _toggleable: true,
    },
    {
      key: 'groupType',
      label: capitalize(group_type),
      _toggleable: true,
    },
    {
      key: 'contact',
      label: capitalize(group_contact_name),
      _toggleable: true,
    },
    {
      key: 'size',
      label: capitalize(guests),
      _toggleable: true,
    },
    {
      key: 'groupGuideName',
      label: capitalize(tour_leader),
      _toggleable: true,
    },
    {
      key: 'groupGuideCabin',
      label: capitalize(tour_leader_cabin),
      _toggleable: true,
    },
    {
      key: 'bookingNumber',
      label: capitalize(booking_no),
      _toggleable: true,
    },
    {
      key: 'salesOffice',
      label: capitalize(sales_office),
      _toggleable: true,
    },
    {
      key: 'agencyContactName',
      label: capitalize(agency_contact_name),
      _toggleable: true,
    },
    {
      key: 'salesPhone',
      label: capitalize(sales_dept_phone),
      _toggleable: true,
    },
    {
      key: 'salesEmail',
      label: capitalize(sales_dept_email),
      _toggleable: true,
    },
    {
      key: 'embark',
      label: capitalize(embark_date),
      _toggleable: true,
    },
    {
      key: 'debark',
      label: capitalize(debark_date),
      _toggleable: true,
    },
    {
      key: 'cabinDrops',
      label: capitalize(cabin_drops),
      _toggleable: true,
    },
    {
      key: 'diningPreference',
      label: capitalize(dining_preference),
      _toggleable: true,
    },
    {
      key: 'guestExperience',
      label: capitalize(guest_experience),
      _toggleable: true,
    },
    {
      key: 'prepaidPackages',
      label: capitalize(prepaid_packages),
      _toggleable: true,
    },
    {
      key: 'notes',
      label: capitalize(group_notes),
      _toggleable: true,
    },
    {
      key: 'hospitalityDesk',
      label: capitalize(hospitality_desk),
      _toggleable: true,
    },
    {
      key: 'balanceDue',
      label: 'Balance Due',
      _toggleable: true,
    },
    {
      key: 'paymentStatus',
      label: 'Payment Status',
      _toggleable: true,
    },
    {
      key: 'actions',
      behavior: BubbleTableFieldBehavior.Condense,
      label: '',
      _visible: true,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<GroupsPartyTableItem>> = [
    {
      key: 'guestExperience',
      render: data => <DefaultedText val={data.guestExperience} html def={'--'} />,
    },
    {
      key: 'notes',
      render: data => <DefaultedText val={data.notes} html def={'--'} />,
    },
    {
      key: 'hospitalityDesk',
      render: data => (
        <FontAwesomeIcon
          icon={data.hospitalityDesk ? 'check' : 'times'}
          className={data.hospitalityDesk ? 'text-lime' : 'text-crimson'}
        />
      ),
    },
    {
      key: 'balanceDue',
      render: data => (
        <DefaultedText val={data.balanceDue ? `$${data.balanceDue}` : ''} def={'--'} />
      ),
    },
    {
      key: 'paymentStatus',
      render: data => <DefaultedText val={capitalizeClean(data.paymentStatus, ['-'])} def={'--'} />,
    },
    {
      key: 'actions',
      render: data => (
        <>
          {canCreateOrEditBookings(user) && (
            <BubbleButton
              onClick={() => setMode({ type: 'group-edit', groupId: data._group._id })}
              className="p-2-i"
              variant="navy"
              inverse
              icon="edit"
            />
          )}
          {canArchiveGroups(user) && (
            <BubbleButton
              onClick={() => handleArchiveGroup(data._group)}
              className="p-2-i"
              variant="dirt"
              inverse
              icon="archive"
            />
          )}
          <BubbleButton
            onClick={() => setMode({ type: 'group-history', group: data._group })}
            className="p-2-i"
            variant="navy"
            inverse
            icon="clock"
          />
          {isExternalOrigin(data._group.party.origin) && (
            <BubbleButton
              onClick={() => {}} // implement copy to clipboard function if needed in the future.
              className="p-2-i"
              variant="smoke"
              inverse
              icon="circle-arrow-up-right"
              title={`origin: ${data._group.party.origin}`}
            />
          )}
        </>
      ),
    },
  ];

  function handleCloseGroupBookingForm() {
    setMode({ type: 'default' });
  }

  async function handleExportGroupBookings() {
    setIsBusy(true);

    try {
      await exportGroups(activeProperty._id);
    } finally {
      setIsBusy(false);
    }
  }

  async function handleArchiveGroup(group: BareSimpleGroupBooking) {
    await setArchived(group._id, !group.archived);
    await fetchData();
  }

  const visibleTableFields = tableFields.filter(tf =>
    tf._visible !== undefined ? tf._visible : filters.visibleFields[tf.key]
  );
  const toggleableTableFields = tableFields.filter(tf => tf._toggleable);

  return (
    <PageWrapper {...props}>
      <Page className="Groups absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu bg-forest-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-4 bg-forest-darker`}>
            <div className="grid gap-2">
              {canCreateOrEditBookings(user) && (
                <BubbleButton
                  variant="lime"
                  className="text-left"
                  block
                  onClick={() => setMode({ type: 'group-edit' })}
                  icon="plus"
                >
                  New Group
                </BubbleButton>
              )}
              <BubbleOverlay loading={isBusy} loadingClassName="rounded" className="flex-grow">
                <BubbleButton
                  variant="slate"
                  className="text-left"
                  block
                  icon="file-export"
                  onClick={handleExportGroupBookings}
                >
                  Export Groups
                </BubbleButton>
              </BubbleOverlay>
            </div>
          </div>
          <BubbleAccordion
            variant="forest"
            className="flex-grow flex flex-col border-t border-forest-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4">
              <div className="text-white flex relative pt-4 px-4">
                <BubbleInput
                  type="checkbox"
                  variant="forest"
                  className="mr-1"
                  inputClassName="stretched-link"
                  checked={showArchived}
                  onValue={checked => {
                    setShowArchived(checked);
                  }}
                />
                <span>Show Archived</span>
              </div>
              <BubbleFormGroup label="Fields" labelClassName="text-white-i" className="px-4 pb-4">
                <div className="grid grid-cols-1">
                  {toggleableTableFields.map(tf => (
                    <div key={tf.key} className="text-white flex relative py-1">
                      <BubbleInput
                        type="checkbox"
                        variant="forest"
                        className="mr-1"
                        inputClassName="stretched-link"
                        checked={filters.visibleFields[tf.key]}
                        onValue={chk =>
                          setFilters({
                            ...filters,
                            visibleFields: { ...filters.visibleFields, [tf.key]: chk },
                          })
                        }
                      />
                      <span>{tf.label || camelToSentenceCase(tf.key)}</span>
                    </div>
                  ))}
                </div>
              </BubbleFormGroup>
            </div>
          </BubbleAccordion>
        </div>
        <div className="GroupsContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="GroupsHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 w-full flex items-end px-3">
              <GroupsNavigation active="groups"></GroupsNavigation>
            </div>
          </div>
          <div className="GroupsBody mx-5 content-minus-logo min-w-900">
            <BubbleOverlay loading={loading}>
              <BubbleTable
                responsive
                striped
                onRowDblClick={data => {
                  if (canCreateOrEditBookings(user)) {
                    setMode({ type: 'group-edit', groupId: data._group._id });
                  }
                }}
                headVariant="forest"
                fields={visibleTableFields}
                items={tableItems}
                keyExtractor={item => item.id}
                stickyHeader={130}
                templates={tableTemplates}
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => (
                  <div className="px-4 pt-4 pb-1 flex items-center">
                    <div className="flex-grow">
                      <BubbleInput
                        variant="forest"
                        inputClassName="outline-none-i"
                        type="text"
                        block
                        value={search}
                        onValue={setSearch}
                        placeholder="Search by Group Name, Group ID, or Booking No."
                      />
                    </div>
                  </div>
                )}
              />
            </BubbleOverlay>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {mode.type !== 'default' && (
          <BubbleDrawer hideTools containerClassName="bg-eggshell">
            {mode.type === 'group-history' ? (
              <GroupPartyHistory mode={mode} onClose={handleCloseGroupBookingForm} />
            ) : (
              <GroupBookingForm
                mode={mode}
                property={activeProperty}
                user={user}
                groupTypes={serverInfo.brand.groupTypes}
                onClose={handleCloseGroupBookingForm}
                onSubmitGroupBookings={fetchData}
              />
            )}
          </BubbleDrawer>
        )}
      </Page>
    </PageWrapper>
  );
};

export default GroupsHome;
